import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question, ModuleId } from '../../types';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { LatexRenderer } from '../ui/LatexRenderer';
import { VisualAidFactory } from '../visuals/VisualAidFactory';
import { Star, AlertCircle, CheckCircle, ArrowRight, Home, BookOpen, Lightbulb, Target, Zap, Bug, XCircle, HelpCircle } from 'lucide-react';
import { CheatSheetDrawer } from './CheatSheetDrawer';
import { ConfettiCelebration } from '../ConfettiCelebration';
import { ErrorLogPanel } from '../ErrorLogPanel';
import { getMotivationalMessage, getStreakMessage } from '../../utils/motivationalMessages';
import { selectSmartQuestion } from '../../utils/knowledgeStateEngine';
import { useAppStore } from '../../hooks/useAppStore';
import { getQuestionsByModule, MODULES } from '../../data/questionLoader';
interface PracticeSessionProps {
  moduleId: ModuleId | null;
  questions: Question[];
  onComplete: () => void;
  onExit: () => void;
  onAnswer: (questionId: string, isCorrect: boolean, confidence: number, userChoice: number) => void;
  cheatSheetViewed: boolean;
  onCheatSheetView: () => void;
  isCrossModule?: boolean;
  skipConfidence?: boolean;
}
type Phase = 'question' | 'confidence' | 'feedback';
// Utility function to shuffle array while preserving original indices
function shuffleOptionsWithIndices(options: string[]): Array<{
  text: string;
  originalIndex: number;
}> {
  const optionsWithIndices = options.map((text, idx) => ({
    text,
    originalIndex: idx
  }));
  // Fisher-Yates shuffle
  for (let i = optionsWithIndices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [optionsWithIndices[i], optionsWithIndices[j]] = [optionsWithIndices[j], optionsWithIndices[i]];
  }
  return optionsWithIndices;
}
// Helper to render explanation steps
const ExplanationSteps = ({
  text
}: {
  text: string;
}) => {
  // Split by newlines to create steps
  const steps = text.split('\n').filter(line => line.trim().length > 0);
  if (steps.length <= 1) {
    return <div className="text-lg leading-relaxed" data-id="element-880">
        <LatexRenderer content={text} data-id="element-881" />
      </div>;
  }
  return <div className="space-y-3" data-id="element-882">
      {steps.map((step, idx) => <div key={idx} className="flex gap-4 items-start" data-id="element-883">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white/60 flex items-center justify-center font-bold text-base border-2 border-current opacity-90 shadow-sm mt-0.5" data-id="element-884">
            {idx + 1}
          </div>
          <div className="flex-1 text-lg leading-relaxed pt-0.5" data-id="element-885">
            <LatexRenderer content={step} data-id="element-886" />
          </div>
        </div>)}
    </div>;
};
export function PracticeSession({
  moduleId,
  questions,
  onComplete,
  onExit,
  onAnswer,
  cheatSheetViewed,
  onCheatSheetView,
  isCrossModule = false,
  skipConfidence = false
}: PracticeSessionProps) {
  const {
    state
  } = useAppStore();
  const [sessionQuestions, setSessionQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('question');
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [shuffledOptions, setShuffledOptions] = useState<Array<{
    text: string;
    originalIndex: number;
  }>>([]);
  const [confidence, setConfidence] = useState<number>(0);
  const [isCheatSheetOpen, setIsCheatSheetOpen] = useState(false);
  const [isErrorLogOpen, setIsErrorLogOpen] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [shakeError, setShakeError] = useState(false);
  const [motivationalText, setMotivationalText] = useState('');
  const [xpGained, setXpGained] = useState(0);
  const [showStreakCelebration, setShowStreakCelebration] = useState(false);
  const [streakMessage, setStreakMessage] = useState('');
  const totalModuleQuestions = moduleId ? getQuestionsByModule(moduleId).length : 0;
  const isFocusMode = isCrossModule || questions.length < totalModuleQuestions && questions.length > 0;
  // ✅ FIXED: Get module title with proper array lookup
  const moduleTitle = moduleId ? MODULES.find(m => m.id === moduleId)?.title || 'Oefenen' : isCrossModule ? 'Superkracht Training' : 'Oefenen';
  // Accessibility: Escape key to exit
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isCheatSheetOpen) {
        onExit();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onExit, isCheatSheetOpen]);
  useEffect(() => {
    if (questions.length > 0 && sessionQuestions.length === 0) {
      if (isCrossModule) {
        setSessionQuestions(questions);
      } else if (moduleId) {
        const recentQuestions = state.progress[moduleId]?.recentQuestions || [];
        const smartQuestions: Question[] = [];
        for (let i = 0; i < Math.min(10, questions.length); i++) {
          const nextQuestionId = selectSmartQuestion(moduleId, state.questionStates, [...recentQuestions, ...smartQuestions.map(q => q.id)]);
          if (nextQuestionId) {
            const question = getQuestionsByModule(moduleId).find(q => q.id === nextQuestionId);
            if (question) {
              smartQuestions.push(question);
            }
          }
        }
        if (smartQuestions.length === 0) {
          const shuffled = [...questions].sort(() => Math.random() - 0.5);
          setSessionQuestions(shuffled.slice(0, 10));
        } else {
          setSessionQuestions(smartQuestions);
        }
      }
    }
  }, [questions, sessionQuestions.length, state.questionStates, state.progress, moduleId, isCrossModule]);
  const currentQuestion = sessionQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === sessionQuestions.length - 1;
  // Shuffle options when question changes
  useEffect(() => {
    if (currentQuestion) {
      setShuffledOptions(shuffleOptionsWithIndices(currentQuestion.options));
    }
  }, [currentQuestion]);
  // LEVEL 3 CONFIDENCE CHECK
  const shouldAskConfidence = !skipConfidence && currentQuestion?.difficulty === 3;
  const handleCheatSheetClose = () => {
    setIsCheatSheetOpen(false);
    if (!cheatSheetViewed) {
      onCheatSheetView();
    }
  };
  const submitAnswer = () => {
    if (selectedOption === null) return;
    // Check if we should ask confidence
    if (shouldAskConfidence) {
      setPhase('confidence');
    } else {
      // Skip confidence for Level 1 & 2
      submitConfidence(5);
    }
  };
  const submitConfidence = (score: number) => {
    if (!currentQuestion) return;
    setConfidence(score);
    // FIXED: Validate shuffledOptions before accessing
    if (!shuffledOptions || shuffledOptions.length === 0 || selectedOption === null) {
      console.error('❌ SHUFFLING ERROR: Invalid shuffledOptions or selectedOption', {
        questionId: currentQuestion.id,
        shuffledOptions,
        selectedOption
      });
      return;
    }
    // FIXED: Safe access to shuffledOptions with validation
    const selectedOptionData = shuffledOptions[selectedOption];
    if (!selectedOptionData) {
      console.error('❌ SHUFFLING ERROR: selectedOption out of bounds', {
        questionId: currentQuestion.id,
        selectedOption,
        shuffledOptionsLength: shuffledOptions.length
      });
      return;
    }
    // Get the original index of the selected option
    const originalIndex = selectedOptionData.originalIndex;
    // FIXED: Validate originalIndex
    if (originalIndex === undefined || originalIndex === null) {
      console.error('❌ SHUFFLING ERROR: originalIndex is undefined', {
        questionId: currentQuestion.id,
        selectedOption,
        selectedOptionData
      });
      return;
    }
    const isCorrect = originalIndex === currentQuestion.correctAnswer;
    // Calculate XP (with Level 3 bonus)
    let xp = 0;
    if (isCorrect) {
      if (currentQuestion.difficulty === 3) {
        xp = score >= 3 ? 25 : 15; // Level 3 bonus
      } else {
        xp = 20; // Standard
      }
    } else {
      xp = 5; // Effort counts
    }
    setXpGained(xp);
    // Update streak
    if (isCorrect) {
      const newStreak = currentStreak + 1;
      setCurrentStreak(newStreak);
      setCorrectCount(prev => prev + 1);
      const message = getStreakMessage(newStreak);
      if (message) {
        setStreakMessage(message);
        setShowStreakCelebration(true);
      }
    } else {
      setCurrentStreak(0);
      setIncorrectCount(prev => prev + 1);
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
    }
    // Generate motivational message
    const message = getMotivationalMessage({
      isCorrect,
      confidence: score,
      streak: currentStreak,
      questionNumber: currentQuestionIndex + 1,
      totalQuestions: sessionQuestions.length
    });
    setMotivationalText(message);
    // Record the answer with original index
    onAnswer(currentQuestion.id, isCorrect, score, originalIndex);
    setPhase('feedback');
  };
  const nextQuestion = () => {
    if (isLastQuestion) {
      onComplete();
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedOption(null);
      setConfidence(0);
      setPhase('question');
    }
  };
  // Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only active during question phase and if cheat sheet is closed
      if (phase !== 'question' || isCheatSheetOpen) return;
      // Number keys 1-4 for options
      if (['1', '2', '3', '4'].includes(e.key)) {
        const index = parseInt(e.key) - 1;
        if (index < shuffledOptions.length) {
          setSelectedOption(index);
        }
      }
      // Enter key to submit
      if (e.key === 'Enter' && selectedOption !== null) {
        submitAnswer();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase, isCheatSheetOpen, shuffledOptions.length, selectedOption]);
  // Get difficulty badge styling
  const getDifficultyBadge = (difficulty?: 1 | 2 | 3) => {
    if (!difficulty) return null;
    const badges = {
      1: {
        label: 'M6',
        color: 'bg-emerald-500',
        icon: null
      },
      2: {
        label: 'M6+',
        color: 'bg-amber-500',
        icon: null
      },
      3: {
        label: 'E6',
        color: 'bg-rose-500',
        icon: <Zap className="w-3 h-3" data-id="element-887" />
      }
    };
    const badge = badges[difficulty];
    return <motion.div initial={{
      scale: 0,
      rotate: -180
    }} animate={{
      scale: 1,
      rotate: 0
    }} transition={{
      type: 'spring',
      stiffness: 200,
      damping: 10
    }} className={`absolute top-6 right-6 ${badge.color} text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-md z-10`} data-id="element-888">
        {badge.icon}
        {badge.label}
      </motion.div>;
  };
  // Empty state
  if (questions.length === 0) {
    return <div className="min-h-screen flex items-center justify-center p-4" data-id="element-889">
        <div className="text-center max-w-md" data-id="element-890">
          <div className="text-6xl mb-4" data-id="element-891">🎉</div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-892">
            Je hebt alle vragen verslagen!
          </h2>
          <p className="text-slate-600 mb-6" data-id="element-893">
            Geweldig werk! Er zijn momenteel geen nieuwe vragen beschikbaar voor
            deze module.
          </p>
          <Button onClick={onExit} variant="primary" size="lg" data-id="element-894">
            Terug naar dashboard
          </Button>
        </div>
      </div>;
  }
  // Loading state
  if (!currentQuestion) {
    return <div className="min-h-screen flex items-center justify-center" role="status" aria-live="polite" data-id="element-895">
        <div className="text-center" data-id="element-896">
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'linear'
        }} className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-4" data-id="element-897" />
          <p className="text-slate-600" data-id="element-898">Slimme vragen selecteren...</p>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50 flex flex-col max-w-3xl mx-auto p-4 md:p-6" data-id="element-899">
      <ConfettiCelebration trigger={showStreakCelebration} message={streakMessage} data-id="element-900" />

      {/* Cheat Sheet Drawer - Always available */}
      <CheatSheetDrawer isOpen={isCheatSheetOpen} onClose={handleCheatSheetClose} moduleId={moduleId!} data-id="element-901" />

      {/* Error Log Panel */}
      <ErrorLogPanel state={state} isOpen={isErrorLogOpen} onClose={() => setIsErrorLogOpen(false)} data-id="element-902" />

      {/* Header */}
      <div className="flex justify-between items-center mb-6" data-id="element-903">
        <motion.button whileHover={{
        scale: 1.1
      }} whileTap={{
        scale: 0.9
      }} onClick={onExit} aria-label="Terug naar dashboard" className="text-slate-400 hover:text-slate-600 transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center" data-id="element-904">
          <Home className="w-6 h-6" data-id="element-905" />
        </motion.button>

        <div className="flex items-center gap-2" data-id="element-906">
          {/* Error Log Button */}
          <motion.button whileHover={{
          scale: 1.1
        }} whileTap={{
          scale: 0.9
        }} onClick={() => setIsErrorLogOpen(true)} aria-label="Open error log" className="text-amber-400 hover:text-amber-600 transition-colors min-h-[48px] flex items-center justify-center gap-2 px-3" data-id="element-907">
            <Bug className="w-5 h-5" data-id="element-908" />
            {state.errorLog && state.errorLog.length > 0 && <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-909">
                {state.errorLog.length}
              </span>}
          </motion.button>

          {/* Cheat Sheet Button */}
          <motion.button whileHover={{
          scale: 1.1
        }} whileTap={{
          scale: 0.9
        }} onClick={() => setIsCheatSheetOpen(true)} aria-label="Open uitleg en spiekbriefje" className="text-indigo-400 hover:text-indigo-600 transition-colors min-h-[48px] flex items-center justify-center gap-2 px-3" data-id="element-910">
            <BookOpen className="w-5 h-5" data-id="element-911" />
            <span className="text-sm font-medium hidden sm:inline" data-id="element-912">Uitleg</span>
          </motion.button>
        </div>

        <div className="flex-1 mx-4 sm:mx-8 h-3 bg-slate-200 rounded-full overflow-hidden shadow-inner" role="progressbar" aria-valuenow={Math.round((currentQuestionIndex + 1) / sessionQuestions.length * 100)} aria-valuemin={0} aria-valuemax={100} aria-label={`Vraag ${currentQuestionIndex + 1} van ${sessionQuestions.length}`} data-id="element-913">
          <motion.div className="h-full bg-indigo-600 rounded-full" initial={{
          width: 0
        }} animate={{
          width: `${(currentQuestionIndex + 1) / sessionQuestions.length * 100}%`
        }} transition={{
          duration: 0.5,
          ease: 'easeInOut'
        }} data-id="element-914" />
        </div>

        <div className="flex items-center gap-3" data-id="element-915">
          {currentStreak > 0 && <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1.5 shadow-sm" data-id="element-916">
              🔥 {currentStreak}
            </motion.div>}
          <span className="text-sm font-bold text-slate-500 bg-white px-3 py-1 rounded-full shadow-sm border border-slate-100" data-id="element-917">
            {currentQuestionIndex + 1}/{sessionQuestions.length}
          </span>
        </div>
      </div>

      {/* Focus Mode Indicator */}
      {isFocusMode && <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-6 bg-indigo-100 border-2 border-indigo-300 rounded-xl p-3 text-center shadow-sm" data-id="element-918">
          <p className="text-sm font-bold text-indigo-900 flex items-center justify-center gap-2" data-id="element-919">
            <Target className="w-4 h-4" data-id="element-920" />
            Superkracht Training
          </p>
          <p className="text-xs text-indigo-700 mt-1" data-id="element-921">
            {isCrossModule ? `Doordenker ${currentQuestionIndex + 1} van ${sessionQuestions.length}` : 'Je oefent nu alleen de doordenkers'}
          </p>
        </motion.div>}

      <AnimatePresence mode="wait" data-id="element-922">
        {/* QUESTION PHASE */}
        {phase === 'question' && <motion.div key={`question-${currentQuestionIndex}`} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.3
      }} className="flex-1 flex flex-col" data-id="element-923">
            <Card className="p-8 md:p-10 mb-6 flex-1 flex flex-col justify-center relative shadow-xl border-slate-100" data-id="element-924">
              {/* Module Title Display */}
              <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="absolute top-6 left-8 text-sm font-bold text-indigo-500 uppercase tracking-wider bg-indigo-50 px-3 py-1 rounded-full" data-id="element-925">
                {moduleTitle}
              </motion.div>

              {/* Difficulty Badge */}
              {getDifficultyBadge(currentQuestion.difficulty)}

              <div className="mt-8" data-id="element-926">
                {currentQuestion.context && <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.15
            }} className="mb-8 p-6 bg-slate-50 rounded-xl border-l-4 border-indigo-400 text-slate-700 italic text-lg shadow-sm" data-id="element-927">
                    <LatexRenderer content={currentQuestion.context} data-id="element-928" />
                  </motion.div>}

                <motion.h2 initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="text-2xl md:text-3xl font-bold text-slate-900 mb-10 leading-snug" data-id="element-929">
                  <LatexRenderer content={currentQuestion.question} data-id="element-930" />
                </motion.h2>

                <div className="grid grid-cols-1 gap-4" data-id="element-931">
                  {shuffledOptions.map((option, idx) => <motion.button key={idx} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.3 + idx * 0.1
              }} whileHover={{
                scale: 1.01,
                x: 4
              }} whileTap={{
                scale: 0.99
              }} onClick={() => setSelectedOption(idx)} className={`
                        p-5 rounded-2xl text-left text-lg font-medium transition-all duration-200 border-2 min-h-[72px] flex items-center group relative
                        ${selectedOption === idx ? 'border-indigo-600 bg-indigo-50 text-indigo-900 shadow-md ring-2 ring-indigo-200 ring-offset-2' : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-slate-50 hover:shadow-sm'}
                      `} data-id="element-932">
                      <span className={`
                        inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold mr-4 transition-colors
                        ${selectedOption === idx ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-indigo-100 group-hover:text-indigo-600'}
                      `} data-id="element-933">
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <span className="flex-1" data-id="element-934">
                        <LatexRenderer content={option.text} data-id="element-935" />
                      </span>
                      {/* ✅ IMPROVED: Keyboard hint - more visible and styled */}
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-md text-xs font-bold text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex items-center gap-1 shadow-sm" data-id="element-936">
                        <kbd className="font-mono" data-id="element-937">{idx + 1}</kbd>
                      </span>
                    </motion.button>)}
                </div>
              </div>
            </Card>

            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} data-id="element-938">
              <Button fullWidth size="lg" disabled={selectedOption === null} onClick={submitAnswer} className="h-14 text-lg shadow-lg hover:shadow-xl transition-shadow relative" data-id="element-939">
                Antwoord Controleren
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-mono bg-white/20 px-2 py-1 rounded hidden md:inline-block" data-id="element-940">
                  Enter
                </span>
              </Button>
            </motion.div>
          </motion.div>}

        {/* CONFIDENCE PHASE (Only for Level 3) */}
        {phase === 'confidence' && shouldAskConfidence && <motion.div key="confidence" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="flex-1 flex flex-col items-center justify-center text-center py-12" data-id="element-941">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} className="mb-8 bg-rose-100 border-2 border-rose-300 rounded-2xl p-6 flex items-center gap-4 shadow-lg" data-id="element-942">
              <div className="bg-rose-200 p-3 rounded-full" data-id="element-943">
                <Zap className="w-8 h-8 text-rose-600" data-id="element-944" />
              </div>
              <div className="text-left" data-id="element-945">
                <p className="font-bold text-rose-900 text-lg" data-id="element-946">E6 Doordenker</p>
                <p className="text-sm text-rose-700" data-id="element-947">Extra uitdaging!</p>
              </div>
            </motion.div>

            <motion.h3 initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-3xl font-bold text-slate-800 mb-4" data-id="element-948">
              Hoe zeker ben je?
            </motion.h3>
            <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-slate-600 mb-12 max-w-md text-lg" data-id="element-949">
              Dit is een pittige vraag! Hoe zeker ben je van je antwoord?
            </motion.p>
            <div className="flex gap-6 mb-12" data-id="element-950">
              {[1, 2, 3, 4, 5].map(star => <motion.button key={star} initial={{
            opacity: 0,
            scale: 0
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: star * 0.1,
            type: 'spring',
            stiffness: 200
          }} whileHover={{
            scale: 1.2,
            rotate: 10
          }} whileTap={{
            scale: 0.9
          }} onClick={() => submitConfidence(star)} className="text-slate-300 hover:text-amber-400 focus:outline-none transition-colors min-h-[64px] min-w-[64px] flex items-center justify-center" data-id="element-951">
                  <Star className="w-16 h-16 fill-current drop-shadow-sm" data-id="element-952" />
                </motion.button>)}
            </div>
            <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="text-slate-500 font-medium" data-id="element-953">
              1 ster = geraden • 5 sterren = heel zeker
            </motion.p>
          </motion.div>}

        {/* FEEDBACK PHASE - REDESIGNED FOR 10-YEAR-OLDS */}
        {phase === 'feedback' && <motion.div key="feedback" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="flex-1 flex flex-col" data-id="element-954">
            {/* LARGE STATUS BADGE */}
            <motion.div initial={{
          scale: 0.8,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 15
        }} className={`
                relative overflow-hidden rounded-3xl p-6 mb-8 text-center shadow-xl border-b-4
                ${selectedOption === currentQuestion.correctAnswer ? 'bg-emerald-500 border-emerald-700 text-white' : 'bg-white border-slate-200 text-slate-900'}
              `} data-id="element-955">
              {selectedOption === currentQuestion.correctAnswer ?
          // CORRECT HEADER
          <>
                  <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20 pointer-events-none" data-id="element-956">
                    <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] bg-[radial-gradient(circle,white,transparent)] animate-spin-slow" data-id="element-957" />
                  </div>
                  <div className="relative z-10 flex flex-col items-center" data-id="element-958">
                    <div className="text-6xl mb-2 animate-bounce" data-id="element-959">🎉</div>
                    <h2 className="text-3xl font-black mb-1 tracking-tight" data-id="element-960">
                      {motivationalText}
                    </h2>
                    <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-1 rounded-full backdrop-blur-sm mt-2" data-id="element-961">
                      <span className="font-bold text-lg" data-id="element-962">+{xpGained} XP</span>
                      <Star className="w-5 h-5 fill-yellow-300 text-yellow-300" data-id="element-963" />
                    </div>
                  </div>
                </> :
          // INCORRECT HEADER
          <div className="flex flex-col items-center" data-id="element-964">
                  <div className="text-6xl mb-2 animate-pulse" data-id="element-965">💪</div>
                  <h2 className="text-3xl font-black text-slate-800 mb-1 tracking-tight" data-id="element-966">
                    {motivationalText}
                  </h2>
                  <p className="text-slate-500 font-medium" data-id="element-967">
                    Geen zorgen, hier leer je van!
                  </p>
                  <div className="inline-flex items-center gap-2 bg-slate-100 px-4 py-1 rounded-full mt-3 text-slate-600" data-id="element-968">
                    <span className="font-bold text-lg" data-id="element-969">+{xpGained} XP</span>
                    <span className="text-xs uppercase tracking-wider" data-id="element-970">
                      voor de inzet
                    </span>
                  </div>
                </div>}
            </motion.div>

            <div className="space-y-6 mb-8" data-id="element-971">
              {/* WRONG ANSWER SECTION (Only if incorrect) */}
              {selectedOption !== null && shuffledOptions[selectedOption]?.originalIndex !== currentQuestion.correctAnswer && <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-972">
                    <Card className="border-2 border-rose-200 bg-rose-50 overflow-hidden" data-id="element-973">
                      <div className="bg-rose-100/50 p-4 border-b border-rose-200 flex items-center gap-3" data-id="element-974">
                        <div className="bg-rose-100 p-2 rounded-xl" data-id="element-975">
                          <AlertCircle className="w-8 h-8 text-rose-600" data-id="element-976" />
                        </div>
                        <h3 className="text-xl font-bold text-rose-900" data-id="element-977">
                          Waarom is dit niet goed?
                        </h3>
                      </div>
                      <div className="p-6" data-id="element-978">
                        <div className="text-rose-900 font-medium mb-4" data-id="element-979">
                          <ExplanationSteps text={currentQuestion.distractorExplanations?.[shuffledOptions[selectedOption]?.originalIndex ?? selectedOption] || 'Helaas, dat was niet het juiste antwoord.'} data-id="element-980" />
                        </div>
                        <div className="bg-white rounded-xl p-4 border-2 border-rose-100 inline-flex items-center gap-3 shadow-sm" data-id="element-981">
                          <span className="text-xs font-bold uppercase tracking-wider text-rose-400" data-id="element-982">
                            Jouw antwoord:
                          </span>
                          <span className="font-bold text-rose-700 text-lg" data-id="element-983">
                            <LatexRenderer content={shuffledOptions[selectedOption]?.text ?? currentQuestion.options[selectedOption]} data-id="element-984" />
                          </span>
                          <XCircle className="w-5 h-5 text-rose-500" data-id="element-985" />
                        </div>
                      </div>
                    </Card>
                  </motion.div>}

              {/* CORRECT ANSWER SECTION */}
              <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-986">
                <Card className="border-2 border-emerald-200 bg-emerald-50 overflow-hidden shadow-lg" data-id="element-987">
                  <div className="bg-emerald-100/50 p-4 border-b border-emerald-200 flex items-center gap-3" data-id="element-988">
                    <div className="bg-emerald-100 p-2 rounded-xl" data-id="element-989">
                      <Lightbulb className="w-8 h-8 text-emerald-600" data-id="element-990" />
                    </div>
                    <h3 className="text-xl font-bold text-emerald-900" data-id="element-991">
                      {selectedOption !== null && shuffledOptions[selectedOption]?.originalIndex === currentQuestion.correctAnswer ? 'Waarom dit klopt' : 'Hoe het wel moet'}
                    </h3>
                  </div>
                  <div className="p-6" data-id="element-992">
                    <div className="text-emerald-900 font-medium mb-6" data-id="element-993">
                      <ExplanationSteps text={currentQuestion.explanation || 'Goed gedaan! Je hebt de juiste strategie gebruikt.'} data-id="element-994" />
                    </div>

                    {selectedOption !== null && shuffledOptions[selectedOption]?.originalIndex !== currentQuestion.correctAnswer && <div className="bg-white rounded-xl p-4 border-2 border-emerald-100 inline-flex items-center gap-3 shadow-sm" data-id="element-995">
                          <span className="text-xs font-bold uppercase tracking-wider text-emerald-400" data-id="element-996">
                            Het juiste antwoord:
                          </span>
                          <span className="font-bold text-emerald-700 text-lg" data-id="element-997">
                            <LatexRenderer content={currentQuestion.options[currentQuestion.correctAnswer]} data-id="element-998" />
                          </span>
                          <CheckCircle className="w-5 h-5 text-emerald-500" data-id="element-999" />
                        </div>}
                  </div>
                </Card>
              </motion.div>

              {/* VISUAL AID */}
              {currentQuestion.visualAid && <>
                  {(!currentQuestion.visualAid.showWhen || currentQuestion.visualAid.showWhen === 'always' || currentQuestion.visualAid.showWhen === 'wrong' && selectedOption !== currentQuestion.correctAnswer || currentQuestion.visualAid.showWhen === 'correct' && selectedOption === currentQuestion.correctAnswer) && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4
            }} data-id="element-1000">
                      <Card className="p-6 border-2 border-indigo-100 shadow-md" data-id="element-1001">
                        <div className="flex items-center gap-2 mb-4 text-indigo-900 font-bold" data-id="element-1002">
                          <HelpCircle className="w-6 h-6 text-indigo-500" data-id="element-1003" />
                          <span data-id="element-1004">Kijk maar eens:</span>
                        </div>
                        <VisualAidFactory visualAid={currentQuestion.visualAid} isCorrect={selectedOption === currentQuestion.correctAnswer} data-id="element-1005" />
                      </Card>
                    </motion.div>}
                </>}
            </div>

            {/* ACTION BUTTON */}
            <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="mt-auto" data-id="element-1006">
              <Button fullWidth size="xl" onClick={nextQuestion} variant={selectedOption !== null && shuffledOptions[selectedOption]?.originalIndex === currentQuestion.correctAnswer ? 'success' : 'primary'} className="h-16 text-xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1" data-id="element-1007">
                {selectedOption !== null && shuffledOptions[selectedOption]?.originalIndex === currentQuestion.correctAnswer ? 'Volgende vraag' : 'Ik snap het nu! 💡'}
                <ArrowRight className="ml-3 w-6 h-6" data-id="element-1008" />
              </Button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}